"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CONTENT_CLUSTER_CONTRACT = exports.ContentClusterContractSchema = exports.REQUIRED_PUBLICATION_COLUMN_KEYS = exports.PublicationColumnContractSchema = exports.PUBLICATION_COLUMN_KEYS = exports.PublicationColumnKeySchema = exports.NON_PUBLIC_PUBLICATION_STATUSES = exports.PUBLISHABLE_PUBLICATION_STATUSES = exports.PUBLICATION_STATUS_OPTION_IDS = exports.PUBLICATION_STATUS_OPTIONS = exports.PublicationTimingSchema = exports.PublicationStatusSchema = void 0;
exports.createPublicationColumnIndex = createPublicationColumnIndex;
exports.isPublicationStatus = isPublicationStatus;
const database_identity_1 = require("@streamlain/database-identity");
const zod_1 = require("zod");
exports.PublicationStatusSchema = zod_1.z.enum([
    'draft',
    'review',
    'approved',
    'scheduled',
    'published',
    'archived',
]);
const PublicationTimestampSchema = zod_1.z
    .string()
    .refine(value => Number.isFinite(Date.parse(value)), 'must be an ISO date or date-time');
exports.PublicationTimingSchema = zod_1.z
    .object({
    status: exports.PublicationStatusSchema,
    publishedAt: PublicationTimestampSchema.optional(),
    scheduledAt: PublicationTimestampSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === 'scheduled' && !value.scheduledAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduled status requires scheduledAt',
        });
    }
    if (value.status !== 'scheduled' && value.scheduledAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is only valid for scheduled status',
        });
    }
    if (value.status === 'published' && !value.publishedAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['publishedAt'],
            message: 'published status requires publishedAt',
        });
    }
    if (['draft', 'review', 'approved'].includes(value.status) &&
        value.publishedAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['publishedAt'],
            message: `${value.status} status cannot have publishedAt`,
        });
    }
});
exports.PUBLICATION_STATUS_OPTIONS = [
    { id: 'draft', value: 'draft', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow },
    { id: 'review', value: 'review', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    {
        id: 'approved',
        value: 'approved',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
    },
    {
        id: 'scheduled',
        value: 'scheduled',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Teal,
    },
    {
        id: 'published',
        value: 'published',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
    },
    {
        id: 'archived',
        value: 'archived',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey,
    },
];
exports.PUBLICATION_STATUS_OPTION_IDS = exports.PUBLICATION_STATUS_OPTIONS.map(option => option.id);
exports.PUBLISHABLE_PUBLICATION_STATUSES = [
    'published',
    'scheduled',
];
exports.NON_PUBLIC_PUBLICATION_STATUSES = [
    'draft',
    'review',
    'approved',
    'archived',
];
exports.PublicationColumnKeySchema = zod_1.z.enum([
    'slug',
    'title',
    'description',
    'body',
    'image',
    'status',
    'publishedAt',
    'scheduledAt',
    'canonical',
    'hreflangGroup',
    'locale',
    'requiredLocale',
    'noindex',
    'clusterId',
    'topicId',
    'internalLinkRowIds',
]);
exports.PUBLICATION_COLUMN_KEYS = {
    SLUG: 'slug',
    TITLE: 'title',
    DESCRIPTION: 'description',
    BODY: 'body',
    IMAGE: 'image',
    STATUS: 'status',
    PUBLISHED_AT: 'publishedAt',
    SCHEDULED_AT: 'scheduledAt',
    CANONICAL: 'canonical',
    HREFLANG_GROUP: 'hreflangGroup',
    LOCALE: 'locale',
    REQUIRED_LOCALE: 'requiredLocale',
    NOINDEX: 'noindex',
    CLUSTER_ID: 'clusterId',
    TOPIC_ID: 'topicId',
    INTERNAL_LINK_ROW_IDS: 'internalLinkRowIds',
};
exports.PublicationColumnContractSchema = zod_1.z
    .object({
    /**
     * Database-local column id. The semantic API field is kept separately so
     * display-name changes can never alter publication behavior.
     */
    field: zod_1.z.string().min(1),
    semanticKey: exports.PublicationColumnKeySchema,
    relation: zod_1.z
        .object({
        targetSourceKey: zod_1.z.string().min(1),
        targetColumnKey: exports.PublicationColumnKeySchema,
    })
        .optional(),
    required: zod_1.z.boolean().optional(),
})
    .strict();
exports.REQUIRED_PUBLICATION_COLUMN_KEYS = [
    exports.PUBLICATION_COLUMN_KEYS.STATUS,
    exports.PUBLICATION_COLUMN_KEYS.SLUG,
    exports.PUBLICATION_COLUMN_KEYS.TITLE,
    exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
];
function createPublicationColumnIndex(columns) {
    const index = {};
    for (const column of columns) {
        const publication = column.data?.['publication'];
        if (!publication || typeof publication !== 'object')
            continue;
        const parsed = exports.PublicationColumnContractSchema.safeParse(publication);
        if (!parsed.success) {
            throw new Error(`Publication binding for column "${column.id}" is invalid: ${parsed.error.issues
                .map(issue => issue.message)
                .join('; ')}`);
        }
        if (parsed.data.field !== column.id) {
            throw new Error(`Publication binding for column "${column.id}" must reference that column id`);
        }
        const semanticKey = parsed.data.semanticKey;
        const existing = index[semanticKey];
        if (existing) {
            throw new Error(`Duplicate publication binding "${semanticKey}" for columns "${existing.id}" and "${column.id}"`);
        }
        index[semanticKey] = column;
    }
    for (const semanticKey of exports.REQUIRED_PUBLICATION_COLUMN_KEYS) {
        if (!index[semanticKey]) {
            throw new Error(`Missing required publication binding "${semanticKey}"`);
        }
    }
    return index;
}
exports.ContentClusterContractSchema = zod_1.z
    .object({
    clusterSourceKey: zod_1.z.string().min(1),
    topicSourceKey: zod_1.z.string().min(1),
    contentSourceKey: zod_1.z.string().min(1),
    routeSourceKey: zod_1.z.string().min(1),
    clusterIdColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.CLUSTER_ID),
    topicIdColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.TOPIC_ID),
    slugColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.SLUG),
    titleColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.TITLE),
    descriptionColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION),
    internalLinksColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.INTERNAL_LINK_ROW_IDS),
})
    .strict();
exports.DEFAULT_CONTENT_CLUSTER_CONTRACT = exports.ContentClusterContractSchema.parse({
    clusterSourceKey: 'clusters',
    topicSourceKey: 'topics',
    contentSourceKey: 'content',
    routeSourceKey: 'pages',
    clusterIdColumnKey: exports.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
    topicIdColumnKey: exports.PUBLICATION_COLUMN_KEYS.TOPIC_ID,
    slugColumnKey: exports.PUBLICATION_COLUMN_KEYS.SLUG,
    titleColumnKey: exports.PUBLICATION_COLUMN_KEYS.TITLE,
    descriptionColumnKey: exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
    internalLinksColumnKey: exports.PUBLICATION_COLUMN_KEYS.INTERNAL_LINK_ROW_IDS,
});
function isPublicationStatus(value) {
    return exports.PublicationStatusSchema.safeParse(value).success;
}
